import { useEffect } from 'react';
import invoiceService from '../../../services/invoiceService';
import quickbooksService from '../../../services/quickbooksService';
import { getFranchiseeId } from '../services/invoiceHelpers';
import { itemsPerPage, getDateRangeFromPreset } from '../utils/invoiceConstants';

export const useInvoiceData = (invoiceState, userProfile, techFilter) => {
  const {
    setInvoices,
    setLoading,
    setError,
    setTotalPages,
    setTotalInvoices,
    setIsQbConnected,
    setIsOnFirstPage,
    currentPage,
    searchTerm,
    filters
  } = invoiceState;

  const fetchInvoices = async () => {
    try {
      setLoading(true);

      const franchiseeId = techFilter?.showFilter ? techFilter.getFilteredFranchiseeId() : getFranchiseeId(userProfile);
      const branchId = techFilter?.getSelectedBranchId?.() || null;
      
      // Resolve date preset to actual from/to values
      const { from: resolvedDateFrom, to: resolvedDateTo } = getDateRangeFromPreset(
        filters.datePreset, filters.dateFrom, filters.dateTo
      );

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        location: filters.location,
        class: filters.class,
        status: filters.status,
        dateFrom: resolvedDateFrom,
        dateTo: resolvedDateTo,
        ...(branchId ? { branch_id: branchId } : {})
      };

      const response = await invoiceService.getInvoices(franchiseeId, params);

      if (response && response.invoices) {
        setInvoices(response.invoices);
        setTotalPages(response.pagination.pages);
        setTotalInvoices(response.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const checkQuickBooksConnection = async () => {
    try {
      const franchiseeId = getFranchiseeId(userProfile);
      
      if (franchiseeId) {
        const hasConnection = await quickbooksService.hasConnection(franchiseeId);
        setIsQbConnected(hasConnection);
      } else {
        setIsQbConnected(false);
      }
    } catch (error) {
      console.error('Error checking QuickBooks connection:', error);
      setIsQbConnected(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    checkQuickBooksConnection();
  }, [currentPage, searchTerm, filters, userProfile, techFilter?.selectedTechnician]);

  // Track if we're on the first page
  useEffect(() => {
    setIsOnFirstPage(currentPage === 1);
  }, [currentPage]);

  return {
    fetchInvoices,
    checkQuickBooksConnection
  };
};